import { createContext, ReactNode, useContext } from 'react'

/**
 * Density of a table/board toolbar's controls (search, Filter/Columns buttons,
 * board quick-filters). Three tiers:
 *  - `md` (default) — historical sizing (38px controls).
 *  - `sm` — DS2026 compact row (32px controls); Targets / Deals / Contacts.
 *  - `xs` — extra-compact row (28px controls) that matches the DS2026
 *    `size="sm"` (h-8) search/filters used on the Replies tab; Campaigns v2.
 *
 * Sibling of {@link TableLinkStyleContext} — the shared toolbar components
 * (`TableSearch`, `TableButton`, the board quick-filter triggers) read this so
 * a single `<ToolbarDensityProvider value="sm">` around a toolbar row opts the
 * whole row into the compact look, without threading a `size` prop through
 * `FilterModal` / `ColumnModal` / the filterset components.
 */
export type ToolbarDensity = 'xs' | 'sm' | 'md'

const ToolbarDensityContext = createContext<ToolbarDensity>('md')

export function ToolbarDensityProvider({
  value,
  children,
}: {
  value: ToolbarDensity
  children: ReactNode
}) {
  return (
    <ToolbarDensityContext.Provider value={value}>
      {children}
    </ToolbarDensityContext.Provider>
  )
}

/** Read the current toolbar density. Defaults to `md` outside any provider. */
export function useToolbarDensity(): ToolbarDensity {
  return useContext(ToolbarDensityContext)
}
